import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  adminSettingsService,
  type AdminSettingKey,
  type AdminSettingsSnapshot,
} from '../../services/adminSettingsService';
import {
  DEFAULT_SITE_SETTINGS,
  type SiteSettings,
  siteSettingsService,
} from '../../services/siteSettingsService';

type SiteSettingKey = keyof SiteSettings;

interface SettingField {
  key: SiteSettingKey;
  label: string;
  description: string;
  placeholder: string;
  type?: 'text' | 'url' | 'email' | 'tel' | 'textarea';
}

interface SettingSection {
  id: string;
  title: string;
  description: string;
  fields: SettingField[];
}

type ProviderKey = 'mollie' | 'postmark';

interface IntegrationSection {
  id: string;
  title: string;
  description: string;
  fields: AdminSettingKey[];
  provider?: ProviderKey;
}

const SETTING_SECTIONS: SettingSection[] = [
  {
    id: 'brand',
    title: 'Identite & navigation',
    description: 'Ce bloc pilote la marque affichee et le CTA principal visible dans le site.',
    fields: [
      {
        key: 'brandShortName',
        label: 'Nom court',
        description: 'Logo texte ou version courte visible dans le header et le footer.',
        placeholder: 'CSD',
      },
      {
        key: 'brandFullName',
        label: 'Nom complet',
        description: 'Nom complet de la marque utilise dans les pages et mentions.',
        placeholder: 'Couteau Suisse Digital',
      },
      {
        key: 'siteUrl',
        label: 'URL du site',
        description: 'URL canonique utilisee comme reference publique.',
        placeholder: 'https://couteausuissedigital.com',
        type: 'url',
      },
      {
        key: 'primaryCtaLabel',
        label: 'Libelle CTA principal',
        description: 'Texte du bouton principal du header et des sections CTA.',
        placeholder: 'Réservez un appel',
      },
      {
        key: 'primaryCtaPath',
        label: 'Lien CTA principal',
        description: 'Chemin interne ou URL du bouton principal.',
        placeholder: '/reserver',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact & support',
    description: 'Informations de contact publiques et blocs support visibles sur le site.',
    fields: [
      {
        key: 'contactEmail',
        label: 'Email principal',
        description: 'Adresse utilisee pour la page contact, les mentions et le footer.',
        placeholder: 'contact@couteausuissedigital.com',
        type: 'email',
      },
      {
        key: 'supportEmail',
        label: 'Email support / vente',
        description: 'Adresse secondaire affichee dans certains blocs commerciaux.',
        placeholder: 'hello@couteausuissedigital.com',
        type: 'email',
      },
      {
        key: 'phoneDisplay',
        label: 'Telephone affiche',
        description: 'Numero visible sur les pages publiques.',
        placeholder: '+41 79 65 38 00',
        type: 'tel',
      },
      {
        key: 'phoneHref',
        label: 'Lien telephone',
        description: 'Lien tel: utilise dans les boutons et liens cliquables.',
        placeholder: 'tel:+4179653800',
      },
    ],
  },
  {
    id: 'forms',
    title: 'Formulaires & automatisations',
    description:
      'Webhooks utilises par les formulaires publics pour envoyer les demandes et reservations.',
    fields: [
      {
        key: 'contactWebhookUrl',
        label: 'Webhook formulaire contact',
        description: 'Endpoint Make ou autre automation pour la page contact.',
        placeholder: 'https://hook.us1.make.com/...',
        type: 'url',
      },
      {
        key: 'bookingWebhookUrl',
        label: 'Webhook reservation appel',
        description: 'Endpoint de la page de reservation d appel.',
        placeholder: 'https://hook.us1.make.com/...',
        type: 'url',
      },
      {
        key: 'bookingTimezone',
        label: 'Fuseau horaire agenda',
        description: 'Fuseau IANA de reference de votre agenda commercial.',
        placeholder: 'America/Toronto',
      },
      {
        key: 'bookingTimezoneLabel',
        label: 'Libelle fuseau horaire',
        description: 'Nom lisible affiche aux visiteurs dans le formulaire.',
        placeholder: 'Toronto',
      },
    ],
  },
  {
    id: 'footer',
    title: 'Footer & reseaux',
    description: 'Textes de bas de page et liens sociaux publics.',
    fields: [
      {
        key: 'footerDescription',
        label: 'Description footer',
        description: 'Accroche courte visible sous la marque.',
        placeholder: 'Votre partenaire dans la transformation digitale',
        type: 'textarea',
      },
      {
        key: 'footerCopyright',
        label: 'Copyright',
        description: 'Mention legale courte du footer.',
        placeholder: '© 2026 Couteau Suisse Digital. Tous droits réservés.',
      },
      {
        key: 'facebookUrl',
        label: 'Facebook',
        description: 'Lien public de la page Facebook.',
        placeholder: 'https://www.facebook.com/...',
        type: 'url',
      },
      {
        key: 'instagramUrl',
        label: 'Instagram',
        description: 'Lien public du compte Instagram.',
        placeholder: 'https://www.instagram.com/...',
        type: 'url',
      },
      {
        key: 'linkedinUrl',
        label: 'LinkedIn',
        description: 'Lien public du compte LinkedIn si utilise.',
        placeholder: 'https://www.linkedin.com/company/...',
        type: 'url',
      },
    ],
  },
  {
    id: 'legal',
    title: 'Legal & entreprise',
    description: 'Informations legales, fiscales et d hebergement affichees dans le site.',
    fields: [
      {
        key: 'legalCompanyName',
        label: 'Entreprise exploitante',
        description: 'Raison sociale legale.',
        placeholder: 'Tybox Network',
      },
      {
        key: 'legalRepresentative',
        label: 'Nom commercial / representant',
        description: 'Nom commercial utilise dans les textes legaux.',
        placeholder: 'Couteau Suisse Digital',
      },
      {
        key: 'legalRegistrationId',
        label: 'Numero d identification',
        description: 'Numero legal / registre / TVA selon votre structure.',
        placeholder: 'CHE-499.459.668',
      },
      {
        key: 'legalAddress',
        label: 'Adresse legale',
        description: 'Adresse ou siege social a afficher.',
        placeholder: 'Rue du Collège 4, 1400 Yverdon-les-Bains, Suisse',
        type: 'textarea',
      },
      {
        key: 'legalCountry',
        label: 'Pays',
        description: 'Pays de rattachement legal.',
        placeholder: 'Suisse',
      },
      {
        key: 'hostingProviderName',
        label: 'Hebergeur',
        description: 'Nom du prestataire d hebergement actuellement utilise.',
        placeholder: 'Ouloulou',
      },
      {
        key: 'hostingProviderAddress',
        label: 'Adresse ou reference hebergement',
        description: 'Adresse postale, reference technique ou host selon vos besoins legaux.',
        placeholder: 'ftp.ouloulou.fr.fo',
        type: 'textarea',
      },
    ],
  },
];

const INTEGRATION_SECTIONS: IntegrationSection[] = [
  {
    id: 'server-general',
    title: 'Base serveur',
    description:
      'Ces reglages alimentent les retours checkout, les liens systeme et les integrations transactionnelles.',
    fields: ['site_url'],
  },
  {
    id: 'mollie',
    title: 'Paiement Mollie',
    description:
      'Ajoute la cle API Mollie pour les paiements, liens de paiement et retours checkout.',
    fields: ['mollie_api_key'],
    provider: 'mollie',
  },
  {
    id: 'postmark',
    title: 'Email transactionnel Postmark',
    description:
      'Configure Postmark pour synchroniser les templates, envoyer les tests et piloter les flows email.',
    fields: [
      'postmark_server_id',
      'postmark_server_token',
      'postmark_from_email',
      'postmark_reply_to',
      'postmark_admin_to',
      'postmark_default_message_stream',
    ],
    provider: 'postmark',
  },
];

const PROVIDER_FIELDS: Record<ProviderKey, AdminSettingKey[]> = {
  mollie: ['site_url', 'mollie_api_key'],
  postmark: [
    'site_url',
    'postmark_server_id',
    'postmark_server_token',
    'postmark_from_email',
    'postmark_reply_to',
    'postmark_admin_to',
    'postmark_default_message_stream',
  ],
};

const createEmptyAdminForm = (): Record<AdminSettingKey, string> => ({
  site_url: '',
  mollie_api_key: '',
  postmark_server_id: '',
  postmark_server_token: '',
  postmark_from_email: '',
  postmark_reply_to: '',
  postmark_admin_to: '',
  postmark_default_message_stream: '',
});

const buildOverviewCards = (settings: SiteSettings) => [
  {
    label: 'Marque',
    value: settings.brandFullName,
    helper: settings.siteUrl,
  },
  {
    label: 'CTA principal',
    value: settings.primaryCtaLabel,
    helper: settings.primaryCtaPath,
  },
  {
    label: 'Contact',
    value: settings.contactEmail,
    helper: settings.phoneDisplay,
  },
  {
    label: 'Entreprise',
    value: settings.legalCompanyName,
    helper: settings.legalRegistrationId,
  },
];

const hydrateAdminForm = (
  snapshot: AdminSettingsSnapshot | null,
  siteUrl: string
): Record<AdminSettingKey, string> => {
  const nextForm = createEmptyAdminForm();

  if (!snapshot) {
    nextForm.site_url = siteUrl;
    return nextForm;
  }

  for (const key of Object.keys(nextForm) as AdminSettingKey[]) {
    nextForm[key] = snapshot.settings[key]?.value || '';
  }

  if (!nextForm.site_url.trim()) {
    nextForm.site_url = siteUrl;
  }

  return nextForm;
};

const inputClasses =
  'mt-2 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition-all duration-200 focus:border-neon-orange focus:bg-white focus:ring-2 focus:ring-neon-orange/10';

const SettingsManager = () => {
  const [form, setForm] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [adminSnapshot, setAdminSnapshot] = useState<AdminSettingsSnapshot | null>(null);
  const [adminForm, setAdminForm] = useState<Record<AdminSettingKey, string>>(createEmptyAdminForm());
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSite, setIsSavingSite] = useState(false);
  const [isSavingIntegrations, setIsSavingIntegrations] = useState(false);
  const [testingProvider, setTestingProvider] = useState<ProviderKey | ''>('');

  const loadAllSettings = async (forceRefresh = false) => {
    setIsLoading(true);

    try {
      const [siteSettings, integrationSnapshot] = await Promise.all([
        siteSettingsService.loadSiteSettings(forceRefresh),
        adminSettingsService.getAdminSettingsSnapshot(),
      ]);

      setForm(siteSettings);
      setAdminSnapshot(integrationSnapshot);
      setAdminForm(hydrateAdminForm(integrationSnapshot, siteSettings.siteUrl));
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Impossible de charger les paramètres'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllSettings().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  const handleChange = (key: SiteSettingKey, value: string) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleAdminChange = (key: AdminSettingKey, value: string) => {
    setAdminForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const refreshAdminSnapshot = async (siteUrlOverride?: string) => {
    const nextSnapshot = await adminSettingsService.getAdminSettingsSnapshot();
    setAdminSnapshot(nextSnapshot);
    setAdminForm(hydrateAdminForm(nextSnapshot, siteUrlOverride || form.siteUrl));
    return nextSnapshot;
  };

  const handleSaveSite = async () => {
    try {
      setIsSavingSite(true);
      const savedSettings = await siteSettingsService.saveSiteSettings(form);
      setForm(savedSettings);
      setAdminForm((current) => ({
        ...current,
        site_url: current.site_url.trim() || savedSettings.siteUrl,
      }));
      toast.success('Paramètres publics du site sauvegardés');
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Impossible de sauvegarder les paramètres'
      );
    } finally {
      setIsSavingSite(false);
    }
  };

  const handleSaveIntegrations = async () => {
    try {
      setIsSavingIntegrations(true);
      const snapshot = await adminSettingsService.saveAdminSettings(adminForm);
      setAdminSnapshot(snapshot);
      setAdminForm(hydrateAdminForm(snapshot, form.siteUrl));
      toast.success('Intégrations serveur sauvegardées');
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Impossible de sauvegarder les intégrations'
      );
    } finally {
      setIsSavingIntegrations(false);
    }
  };

  const handleTestProvider = async (provider: ProviderKey) => {
    try {
      setTestingProvider(provider);

      const scopedPayload = PROVIDER_FIELDS[provider].reduce(
        (acc, key) => {
          const value = adminForm[key]?.trim();
          if (value) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Partial<Record<AdminSettingKey, string>>
      );

      if (Object.keys(scopedPayload).length > 0) {
        const snapshot = await adminSettingsService.saveAdminSettings(scopedPayload);
        setAdminSnapshot(snapshot);
        setAdminForm(hydrateAdminForm(snapshot, form.siteUrl));
      }

      const result = await adminSettingsService.testProviderConnection(provider);
      await refreshAdminSnapshot(form.siteUrl);
      toast.success(result.message);
    } catch (error) {
      console.error(error);
      try {
        await refreshAdminSnapshot(form.siteUrl);
      } catch (refreshError) {
        console.error(refreshError);
      }

      toast.error(
        error instanceof Error ? error.message : `Impossible de tester ${provider}`
      );
    } finally {
      setTestingProvider('');
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-gray-600">Chargement des paramètres...</div>;
  }

  const overviewCards = buildOverviewCards(form);

  return (
    <div className="space-y-8 p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Paramètres du site & intégrations</h2>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-gray-600">
            Cette page pilote maintenant les réglages publics du site et les vraies intégrations
            serveur utilisées sur ton hébergement Apache: Mollie, Postmark, expéditeur email,
            streams et URL de retour.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => loadAllSettings(true)}
            className="rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            Rafraîchir
          </button>
          <button
            onClick={handleSaveSite}
            disabled={isSavingSite}
            className="rounded-full bg-gray-900 px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSavingSite ? 'Sauvegarde site...' : 'Sauvegarder le site'}
          </button>
          <button
            onClick={handleSaveIntegrations}
            disabled={isSavingIntegrations}
            className="rounded-full bg-neon-orange px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-neon-orange/20 transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSavingIntegrations ? 'Sauvegarde intégrations...' : 'Sauvegarder Mollie / Postmark'}
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {overviewCards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
          >
            <div className="text-sm text-gray-500">{card.label}</div>
            <div className="mt-2 text-xl font-bold text-gray-900">{card.value}</div>
            <div className="mt-2 text-xs text-gray-500">{card.helper}</div>
          </div>
        ))}
      </div>

      <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <h3 className="text-xl font-semibold text-gray-900">Intégrations serveur</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Tes identifiants Mollie et Postmark sont maintenant gérés ici pour l&apos;admin live.
              Les tests et la synchronisation Postmark passent par le serveur Apache du site, plus
              par les anciennes routes <code>/api</code>.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleTestProvider('mollie')}
              disabled={testingProvider !== ''}
              className="rounded-full border border-sky-200 bg-sky-50 px-5 py-3 text-sm font-semibold text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-60"
            >
              {testingProvider === 'mollie' ? 'Test Mollie...' : 'Tester Mollie'}
            </button>
            <button
              onClick={() => handleTestProvider('postmark')}
              disabled={testingProvider !== ''}
              className="rounded-full border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-60"
            >
              {testingProvider === 'postmark' ? 'Test Postmark...' : 'Tester Postmark'}
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="text-sm text-gray-500">Mollie</div>
            <div className="mt-2 text-xl font-bold text-gray-900">
              {adminSnapshot?.providers.mollie.configured ? 'Configuré' : 'Non configuré'}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {adminSnapshot?.providers.mollie.lastTestMessage || 'Aucun test encore lancé.'}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="text-sm text-gray-500">Postmark</div>
            <div className="mt-2 text-xl font-bold text-gray-900">
              {adminSnapshot?.providers.postmark.configured ? 'Configuré' : 'Non configuré'}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {adminSnapshot?.providers.postmark.lastTestMessage || 'Aucun test encore lancé.'}
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="text-sm text-gray-500">URL serveur active</div>
            <div className="mt-2 text-xl font-bold text-gray-900 break-all">
              {adminForm.site_url || form.siteUrl}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Base utilisée pour Mollie et les liens email.
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="text-sm text-gray-500">Message stream</div>
            <div className="mt-2 text-xl font-bold text-gray-900">
              {adminForm.postmark_default_message_stream || 'checkout-automation'}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Stream utilisé par défaut pour les templates Postmark.
            </div>
          </div>
        </div>
      </section>

      {INTEGRATION_SECTIONS.map((section) => (
        <section
          key={section.id}
          className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="max-w-3xl">
            <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{section.description}</p>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {section.fields.map((key) => {
              const field = adminSnapshot?.settings[key];
              if (!field) {
                return null;
              }

              const isSensitive = field.sensitive;
              const inputType = key.includes('email')
                ? 'email'
                : key.includes('url')
                  ? 'url'
                  : isSensitive
                    ? 'password'
                    : 'text';

              return (
                <label key={key} className="block">
                  <span className="text-sm font-semibold text-gray-800">{field.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">
                    {field.description}
                  </span>
                  <input
                    type={inputType}
                    value={adminForm[key]}
                    onChange={(event) => handleAdminChange(key, event.target.value)}
                    placeholder={field.placeholder}
                    className={inputClasses}
                    autoComplete="off"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span>
                      {field.configured
                        ? `Configuré${field.updatedAt ? ` le ${new Date(field.updatedAt).toLocaleString('fr-FR')}` : ''}`
                        : 'Pas encore configuré'}
                    </span>
                    {isSensitive && field.maskedValue ? (
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-600">
                        Valeur masquée: {field.maskedValue}
                      </span>
                    ) : null}
                    {field.lastTestStatus ? (
                      <span
                        className={`rounded-full px-3 py-1 ${
                          field.lastTestStatus === 'success'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {field.lastTestStatus === 'success' ? 'Test valide' : 'Test en erreur'}
                      </span>
                    ) : null}
                  </div>
                  {field.lastTestMessage ? (
                    <div className="mt-2 text-xs leading-5 text-gray-500">
                      Dernier retour: {field.lastTestMessage}
                    </div>
                  ) : null}
                </label>
              );
            })}
          </div>

          {section.provider ? (
            <div className="mt-6">
              <button
                onClick={() => handleTestProvider(section.provider as ProviderKey)}
                disabled={testingProvider !== ''}
                className="rounded-full border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
              >
                {testingProvider === section.provider
                  ? `Test ${section.provider}...`
                  : `Tester ${section.provider === 'mollie' ? 'la connexion Mollie' : 'la connexion Postmark'}`}
              </button>
            </div>
          ) : null}
        </section>
      ))}

      {SETTING_SECTIONS.map((section) => (
        <section
          key={section.id}
          className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="max-w-3xl">
            <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">{section.description}</p>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {section.fields.map((field) => {
              const isTextarea = field.type === 'textarea';
              const fieldValue = form[field.key];

              return (
                <label
                  key={field.key}
                  className={isTextarea ? 'block lg:col-span-2' : 'block'}
                >
                  <span className="text-sm font-semibold text-gray-800">{field.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">
                    {field.description}
                  </span>
                  {isTextarea ? (
                    <textarea
                      value={fieldValue}
                      onChange={(event) => handleChange(field.key, event.target.value)}
                      rows={4}
                      placeholder={field.placeholder}
                      className={`${inputClasses} min-h-[124px] resize-y`}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={fieldValue}
                      onChange={(event) => handleChange(field.key, event.target.value)}
                      placeholder={field.placeholder}
                      className={inputClasses}
                    />
                  )}
                </label>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default SettingsManager;
